import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBook, FaSchool, FaMapMarkerAlt, FaArrowRight, FaGraduationCap } from 'react-icons/fa';
import { RiBookOpenLine, RiBuilding2Line, RiFlashlightLine, RiTeamLine } from 'react-icons/ri';

// Add CSS animation styles
const backgroundAnimationStyles = `
  @keyframes float {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  .floating-bg {
    animation: float 15s ease-in-out infinite;
    will-change: transform;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const floatAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const HomePage = () => {
  return (
    <div className="min-h-screen">
      <style>{backgroundAnimationStyles}</style>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary-dark to-primary py-20 md:py-32">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl floating-bg"
          />
          <div
            className="absolute -bottom-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl floating-bg"
            style={{ animationDelay: '-7.5s' }}
          />
        </div>

        <div className="container mx-auto px-4 text-center relative">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight"
              variants={itemVariants}
            >
              Every Child Deserves
              <span className="block text-primary-light">A Great Book</span>
            </motion.h1>

            <motion.p 
              className="text-lg md:text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              We're bringing the joy of reading to schools across London with our interactive platform. Join us in making reading accessible and exciting for every student.
            </motion.p>
            
            {/* Statistics */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto"
              variants={itemVariants}
            >
              <motion.div 
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-white border border-white/20"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex items-center justify-center mb-3">
                  <div className="bg-primary-light/20 p-3 rounded-xl">
                    <FaBook className="text-2xl" />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">1000+</div>
                <div className="text-white/80">Books Available</div>
              </motion.div>

              <motion.div 
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-white border border-white/20"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex items-center justify-center mb-3">
                  <div className="bg-primary-light/20 p-3 rounded-xl">
                    <FaSchool className="text-2xl" />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">19</div>
                <div className="text-white/80">Partner Schools</div>
              </motion.div>

              <motion.div 
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-white border border-white/20"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex items-center justify-center mb-3">
                  <div className="bg-primary-light/20 p-3 rounded-xl">
                    <FaMapMarkerAlt className="text-2xl" />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">North & West</div>
                <div className="text-white/80">London Coverage</div>
              </motion.div>
            </motion.div>
            
            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              variants={itemVariants}
            >
              <Link 
                to="/login" 
                className="bg-gradient-to-r from-green-400 to-green-500 text-white font-semibold text-lg inline-flex items-center justify-center py-3 px-10 rounded-full shadow-md transition-all duration-300 hover:scale-105"
              >
                Go to Student Portal <FaArrowRight className="ml-2" />
              </Link>
              <Link 
                to="/howto" 
                className="bg-white text-blue-500 font-semibold text-lg inline-flex items-center justify-center py-3 px-10 rounded-full shadow-md transition-all duration-300 hover:scale-105"
              >
                How to Use <FaArrowRight className="ml-2" />
              </Link>
              <Link 
                to="/about" 
                className="bg-gradient-to-r from-primary to-purple-500 text-white font-semibold text-lg inline-flex items-center justify-center py-3 px-10 rounded-full shadow-md transition-all duration-300 hover:scale-105"
              >
                About Our Mission <FaArrowRight className="ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-[32px] md:text-[36px] font-bold mb-4 gradient-text tracking-tight">Our Mission</h2>
            <p className="text-lg text-[#3D3D3D] max-w-3xl mx-auto leading-relaxed">
              Page4Change is dedicated to fostering a love of reading in young people across London. We believe that 
              access to engaging books and interactive learning tools can transform educational outcomes and inspire 
              lifelong reading habits.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="card card-hover"
            >
              <div className="bg-primary/10 p-4 rounded-2xl mb-6 inline-block">
                <RiBookOpenLine className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Diverse Reading</h3>
              <p className="text-gray-600">
                We curate diverse collections of books that represent different cultures, perspectives, and genres to engage all readers.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="card card-hover"
            >
              <div className="bg-primary/10 p-4 rounded-2xl mb-6 inline-block">
                <RiBuilding2Line className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">School Partnerships</h3>
              <p className="text-gray-600">
                We partner with 19 schools across London to deliver our programs and ensure books reach students who need them most.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="card card-hover"
            >
              <div className="bg-primary/10 p-4 rounded-2xl mb-6 inline-block">
                <RiFlashlightLine className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Interactive Learning</h3>
              <p className="text-gray-600">
                Our AI-powered chat functionality allows students to engage with books on a deeper level through conversations about the content.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="card card-hover"
            >
              <div className="bg-primary/10 p-4 rounded-2xl mb-6 inline-block">
                <RiTeamLine className="text-3xl text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Community Impact</h3>
              <p className="text-gray-600">
                We're building a community of readers and educators who share our passion for making reading accessible and enjoyable for all.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Community & Partnerships Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-[32px] md:text-[36px] font-bold text-[#4F6EDF] mb-4 text-center tracking-tight">Building a Culture of Reading</h2>
          <p className="text-lg text-[#3D3D3D] mb-8 text-center leading-relaxed">
            At Page4Change, we work closely with schools across London to ensure that every student has access to inclusive, engaging, and culturally conscious reading materials. We believe that every child deserves books that reflect their identity, broaden their worldview, and inspire a lifelong love of reading.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-primary/5 rounded-2xl p-6 shadow">
              <h3 className="font-semibold text-primary mb-2">Community Reading Drives</h3>
              <p className="text-gray-600">
                In addition to supporting schools, we lead local reading drives in our communities, collecting and distributing books directly to children and families who might not otherwise have access. These drives are part of our broader mission to make reading a shared and celebrated part of daily life.
              </p>
            </div>
            <div className="bg-secondary-green/5 rounded-2xl p-6 shadow">
              <h3 className="font-semibold text-secondary-green mb-2">Partnerships for Impact</h3>
              <p className="text-gray-600">
                We also partner with educators, libraries, and literacy-focused charities to build sustainable networks of support for young readers. Our team curates book selections tailored to the needs of each community, ensuring that the resources we provide are not only educational but meaningful and relevant.
              </p>
            </div>
            <div className="bg-orange-100 rounded-2xl p-6 shadow">
              <h3 className="font-semibold text-orange-500 mb-2">Inclusive Book Curation</h3>
              <p className="text-gray-600">
                Every book we provide is chosen to reflect the diversity of our communities, helping children see themselves in stories and discover new perspectives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-[32px] md:text-[36px] font-bold text-[#4F6EDF] mb-8 text-center tracking-tight">Benefits for Teachers & Students</h2>
          <div className="grid md:grid-cols-2 gap-14">
            {/* Teacher Benefits */}
            <div className="bg-[#FAFBFE] rounded-2xl p-8 border border-[#E1E8FF] shadow-sm">
              <h3 className="text-[22px] font-semibold text-[#4F6EDF] mb-6">For Teachers</h3>
              <ul className="space-y-6">
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Access to Curated, Culturally Relevant Materials:</span> Save time with pre-screened books that reflect diverse voices, align with curriculum goals, and resonate with students' backgrounds.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Supports Literacy Goals:</span> Our platform complements classroom learning with targeted reading tools that help improve comprehension, vocabulary, and critical thinking.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Student Engagement Tools:</span> Use interactive features like read-along modes, student-led discussions, and AI-driven character Q&A to motivate reluctant readers.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Data-Driven Insights:</span> Track student progress, reading habits, and areas for improvement through simple analytics dashboards that inform teaching strategies.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Customisable Content:</span> Select texts by theme, reading level, or learning outcome — ideal for differentiated instruction and tailored lesson planning.
                  </div>
                </li>
              </ul>
            </div>
            
            {/* Student Benefits */}
            <div className="bg-[#FAFBFE] rounded-2xl p-8 border border-[#E1E8FF] shadow-sm">
              <h3 className="text-[22px] font-semibold text-[#4F6EDF] mb-6">For Students</h3>
              <ul className="space-y-6">
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Access to Books They Can Relate To:</span> Read stories that reflect their culture, experiences, and identities, helping them feel seen and valued.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Improved Literacy and Confidence:</span> By combining engaging narratives with guided reading support, students build stronger literacy skills and feel more confident as readers.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Interactive Reading Experience:</span> Read alongside an AI voice, ask questions to characters, and explore stories in a more immersive, conversational way.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Increased Motivation and Autonomy:</span> Students develop ownership of their learning journey through personalized reading paths and self-paced exploration.
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-[#4F6EDF] group-hover:fill-[#4F6EDF]/30 transition-all duration-100" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                    </svg>
                  </div>
                  <div>
                    <span className="font-semibold text-[#3D3D3D]">Peer Learning Opportunities:</span> Engage in group discussions powered by shared reading goals and collaborative learning experiences.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Student CTA Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-primary-light py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl floating-bg"
          />
          <div
            className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl floating-bg"
            style={{ animationDelay: '-7.5s' }}
          />
        </div>

        <div className="container mx-auto px-4 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Reading Journey?</h2>
            <p className="text-white/90 text-lg mb-10 leading-relaxed">
              Our student portal has all the books available at your school, with interactive features to help 
              you explore and discuss them. Join thousands of students who are already discovering the joy of reading.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/login" 
                className="bg-gradient-to-r from-green-400 to-green-500 text-white font-semibold text-lg inline-flex items-center justify-center py-3 px-10 rounded-full shadow-md transition-all duration-300 hover:scale-105"
              >
                Go to Student Portal <FaArrowRight className="ml-2" />
              </Link>
              <Link 
                to="/howto" 
                className="btn-secondary inline-flex items-center justify-center"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 